import { useEffect, useMemo, useRef } from 'react';

import { useMemberNodeId } from '@trello/business-logic-react/member';
import { plannerDevToolsState } from '@trello/business-logic/planner';

import { usePlannerMemberAccountFragment } from './PlannerMemberAccountFragment.generated';

/**
 * @migrationInProgress This hook is likely to change significantly as we migrate to multi-account support.
 * The current implementation assumes a single account per user, which will be updated in future versions.
 *
 * Custom hook to retrieve the enabledCalendars, plannerAccountID, display name, and outboundAuthId
 * for the current member, along with providing pagination information for enabled calendars.
 *
 * This hook uses the `useMemberId` hook to get the current member's ID,
 * then fetches the member's node ID using the `useMemberNodeIdFragment` hook,
 * and finally retrieves the enabledCalendars, plannerAccountID, display name, and
 * outboundAuthId using the `usePlannerAccountFragment` hook.
 *
 * The enabledCalendars are paginated, so this hook also provides a `isLoadingEnabledCalendars`
 * boolean to indicate if there are more enabled calendars to fetch.  This piece of the hook
 * is used in the `ConnectedCalendarsButton` component to display the connected calendars.
 *
 * If returnExpiredAccount is provided as true, the hook will return the first planner account
 * even if it is expired.
 *
 * @returns enabledCalendars, isLoadingEnabledCalendars, the planner account ID, display name,
 * and outbound auth id for the current user, or undefined.
 */
export const usePlannerAccount = ({
  returnExpiredAccount = false,
}: {
  returnExpiredAccount?: boolean;
} = {}) => {
  const memberNodeId = useMemberNodeId();

  const isLoadingEnabledCalendars = useRef(true);

  const { data: plannerAccountData } = usePlannerMemberAccountFragment({
    from: { id: memberNodeId },
    optimistic: true,
  });

  // We can simply filter for the planner account with required scopes here, since usePlannerData
  // will handle showing the overlay if the only connected accounts do not have the required scopes
  // Use the first account if no planner account has scopes and isnt expired
  const validPlannerAccount =
    plannerAccountData?.planner?.accounts?.edges?.find(
      (edge) => edge?.node?.hasRequiredScopes && !edge?.node?.isExpired,
    )?.node;

  const firstPlannerAccount =
    plannerAccountData?.planner?.accounts?.edges?.[0]?.node;

  // If returnExpiredAccount is true, return the first planner account even if it is expired
  // Planner.tsx relies on the plannerAccountId to determine whether or not a valid account is connected,
  // so we by default will not return an expired / invalid account.
  const plannerAccount = returnExpiredAccount
    ? validPlannerAccount || firstPlannerAccount
    : validPlannerAccount;

  const enabledCalendars = useMemo(() => {
    return (
      plannerAccount?.enabledCalendars?.edges?.map(({ node }) => node) ||
      undefined
    );
  }, [plannerAccount]);

  const providerCalendar = useMemo(() => {
    return plannerAccount?.providerCalendars || undefined;
  }, [plannerAccount]);

  // To persist the total enabled calendars count through re-renders
  // need to set the ref to false when there are no more enabled calendars.
  useEffect(() => {
    if (plannerAccount?.enabledCalendars?.pageInfo?.hasNextPage === false) {
      isLoadingEnabledCalendars.current = false;
    }
  }, [plannerAccount]);

  plannerDevToolsState.setValue({
    ...plannerDevToolsState,
    accountId: plannerAccount?.id || '',
  });

  return {
    account: plannerAccount,
    accountType: plannerAccount?.accountType || undefined,
    enabledCalendars,
    isLoadingEnabledCalendars: plannerAccount
      ? isLoadingEnabledCalendars.current
      : undefined,
    outboundAuthId: plannerAccount?.outboundAuthId || undefined,
    plannerAccountDisplayName: plannerAccount?.displayName || undefined,
    plannerAccountId: plannerAccount?.id || undefined,
    providerCalendar,
  };
};
